/* eslint-disable max-params */
import {createInteractionCallback, HEADER} from '@optimaxdev/analytics/mobile';
import {sendAnalytics} from 'libs/analytics/sendAnalytics';
import {LinkMenu} from 'features/menu/components/links/link/link';
import {PureLink} from 'features/menu/components/links/pureLink/pureLink';
import {BrandLink} from 'features/menu/components/links/brandLink/brandLink';
import {ServiceLink} from 'features/menu/components/links/serviceLink/serviceLink';
import {Accessibility} from 'features/menu/components/links/accessibility/accessibility';
import {Collapse} from 'features/menu/components/collapse/collapse';
import {SingleMenuLink} from 'features/menu/components/links/singleLink/singleLink';

/**
 * Universal type for mobile menu structure item
 */
export type StructureItemType = {
    component: Node,
    props: {
        to?: string,
        brand?: string,
        content?: string,
        icon?: string,
        color?: string,
    },
    children: Array<StructureItemType> | Array,
};

/**
 * Single link arguments
 */
export type SingleLinkArgs = {
    to: string,
    content: string,
    className: string,
    icon: string,
    analyticsData: null | {buttonName: string, label?: string},
};

/**
 * Returns prepared structure component for link to brand
 *
 * @param {string} to - Target brand URL
 * @param {string} brandName - Target brand name
 * @returns {StructureItemType} - Prepared structure item
 */
export const brand = (to: string, brandName: string): StructureItemType => ({
    component: BrandLink,
    props: {to, brandName},
    children: [],
});

/**
 * Returns prepared structure component for Link
 *
 * @param {string} to - Target URL
 * @param {string} content - Target text content
 * @returns {StructureItemType} - Prepared structure item
 */
export const link = (to: string, content: string): StructureItemType => ({
    component: LinkMenu,
    props: {to, content},
    children: [],
});

/**
 * Returns prepared structure component for Link used as stand-alone menu item
 *
 * @param {SingleLinkArgs} - Single link arguments
 * @returns {StructureItemType} - Prepared structure item
 */
export const singleLink = ({
    to,
    content,
    className = 'bold',
    icon = '',
    analyticsData = null,
}: SingleLinkArgs): StructureItemType => ({
    component: SingleMenuLink,
    props: {
        to,
        content,
        className,
        icon,
        onClick: createInteractionCallback(HEADER.BUTTON, {
            version: 'M',
            buttonName: analyticsData ? analyticsData.buttonName : content,
            label: analyticsData && analyticsData.label ? analyticsData.label : '',
        }),
    },
    children: [],
});

/**
 * Returns prepared structure component for simple link
 *
 * @param {string} to - Target URL
 * @param {string} content - Target text content
 * @param {string} className - Target additional className (if already exist in serviceLink.scss)
 * @param {string} icon - Target icon (if needed)
 * @returns {StructureItemType} - Prepared structure item
 */
export const pureLink = (
    to: string,
    content: string,
    className: string = '',
    icon: string = '',
): StructureItemType => ({
    component: PureLink,
    props: {
        to,
        content,
        className,
        icon,
    },
    children: [],
});

/**
 * Returns prepared structure component for list of structure items
 *
 * @param {string} content - List text content
 * @param {Array<StructureItemType>} children - List of list children
 * @returns {StructureItemType} - Prepared structure item
 */
export const list = (content: string, children: Array<StructureItemType>): StructureItemType => {
    const sendAnalyticsCallback = subCategory => {
        sendAnalytics(HEADER.MENU, {
            category: content,
            subCategory,
        });
    };
    const childrenWithCallback = children.map(child => {
        const {content: childContent, brandName} = child.props;
        const label = brandName
            ? brandName.charAt(0).toUpperCase() + brandName.slice(1)
            : childContent;
        return {
            ...child,
            props: {...child.props, onClick: () => sendAnalyticsCallback(label)},
        };
    });
    return {
        component: Collapse,
        props: {content, onOpen: () => sendAnalyticsCallback('Category Clicked')},
        children: childrenWithCallback,
    };
};

/**
 * Returns prepared structure component for service links (e.g. Help Center or Clearance pages)
 * with icons and additional classNames if needed
 *
 * @param {string} to - Target URL
 * @param {string} content - Target text content
 * @param {string} icon - Target icon (if needed)
 * @param {string} className - Target additional className (if already exist in serviceLink.scss)
 * @returns {StructureItemType} - Prepared structure item
 */
export const service = (
    to: string,
    content: string,
    icon: string = '',
    className: string = '',
): StructureItemType => ({
    component: ServiceLink,
    props: {
        to,
        content,
        icon,
        className,
    },
    children: [],
});

/**
 * Returns prepared structure component for empty item without props and children. Just returns component.
 *
 * @param {Node} component - Received component which will be rendered without props by default
 * @param {Object} props - Received props for component
 * @returns {StructureItemType} - Prepared structure item
 */
export const item = (component: Node, props: {} = {}): StructureItemType => ({
    component,
    props,
    children: [],
});

/**
 * disabledLink
 * @returns {{component: Accessibility, children: Array}} - component
 */
export const disabledLink = () => ({
    component: Accessibility,
    children: [],
});
